import { useEffect, useState, type ReactNode } from 'react';
import type { StoreKind } from '../store/Store';

// All event times are US Eastern. The role-bar clock shows the real current
// wall time in that zone (hourCycle h23 → 00–23, never "24:00"), seconds
// hidden. Note: this is the live clock only — scheduled_at values keep the
// "wall-clock-as-UTC" convention and are still formatted with UTC accessors
// elsewhere; the two are independent.
const ET_CLOCK = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

function useEasternClock(): string {
  const [now, setNow] = useState(() => ET_CLOCK.format(new Date()));
  useEffect(() => {
    const id = setInterval(() => setNow(ET_CLOCK.format(new Date())), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

const STORE_DOT: Record<StoreKind, { color: string; title: string }> = {
  mock:     { color: 'var(--warn)', title: 'Mock data — in-memory MockStore; nothing persists.' },
  snapshot: { color: 'var(--info)', title: 'Cached — read-only spectator snapshot (~10s edge cache).' },
  supabase: { color: 'var(--ok)',   title: 'Live DB — Supabase Postgres + Realtime.' },
};

export function ProtoTopbar({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: ReactNode;
}) {
  return (
    <header className="proto-topbar row between gap-4 px-5 py-3 bg-1 border-b">
      <div className="min-w-0" style={{ flex: '0 1 auto' }}>
        <div className="display txt-22 truncate tracking-1 lh-tight">{title}</div>
        {subtitle && (
          <div className="proto-topbar-subtitle mono txt-11 text-2 mt-1 tracking-2 truncate">
            {subtitle}
          </div>
        )}
      </div>
      <div className="proto-topbar-right row gap-2 shrink-0">
        {right}
      </div>
    </header>
  );
}

// ProtoClockChip — wall clock + per-event delay status.
//
// `event` (optional) tags the delay portion so a viewer can tell which
// schedule the chip is reporting on. When event is omitted, only the wall
// clock is shown (e.g. App-level role-bar where there's no specific event
// context).
//
// `hideTime` (optional) suppresses the wall-clock portion. The per-screen
// topbars (Schedule, Results) on both mobile and desktop pass this so the
// in-page chip doesn't duplicate the wall clock that's already shown in
// the role-bar above. When hideTime is true and there's no delay info,
// the chip renders nothing.
export function ProtoClockChip({ delayMin, event, hideTime, status }: {
  delayMin?: number;
  event?: '3v3' | '1v1';
  hideTime?: boolean;
  // When set (the role-bar clock), the dot reflects the data source instead of
  // delay: orange = mock, green = live DB, light-blue = cached snapshot.
  status?: StoreKind;
}) {
  const now = useEasternClock();
  const showDelay = typeof delayMin === 'number';
  const statusDot = status ? STORE_DOT[status] : undefined;
  const dotColor = statusDot ? statusDot.color
    : !showDelay ? 'var(--ok)' : (delayMin! > 0 ? 'var(--warn)' : 'var(--ok)');
  // Nothing to show when both time and delay are suppressed.
  if (hideTime && !showDelay) return null;
  return (
    <div className="row gap-2 pad-btn-sm r-md bg-2 border">
      <span title={statusDot?.title} style={{ width: 6, height: 6, borderRadius: 999, background: dotColor }} />
      {!hideTime && (
        <span className="mono txt-12 fw-semi">{now} ET</span>
      )}
      {showDelay && (
        <>
          {!hideTime && <span className="txt-11 text-2">•</span>}
          {event && (
            <span className="mono txt-10 text-2 tracking-2">
              {event.toUpperCase()}
            </span>
          )}
          {delayMin! > 0 ? (
            <span className="mono txt-11 text-warn">+{delayMin}m DELAY</span>
          ) : (
            <span className="mono txt-11 text-2">ON SCHEDULE</span>
          )}
        </>
      )}
    </div>
  );
}
